import { useBooleanState, useQueryParams } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { buildQuery } from '@calendly/http-tools';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { ArrowRoundRightIcon, DeleteIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, forwardRef } from 'react';
import { useHistory } from 'react-router-dom';
import { HostReschedule } from 'features/host_reschedule';
import { useNotetakerEntitled } from 'features/meeting_intelligence';
import { GenerateMeetingPrep, useGenerateMeetingPrep } from 'features/meeting_prep';
import { getEventLocationIfURL } from 'features/meetings';
import { useTrack, TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { isCalendlyMeeting } from 'shared/utils';
import { AddNotetakerButton, useNotetaker } from './add_notetaker_button';
import { AiRecapButton } from './ai_recap_button';
import { BookFollowUpMeetingButton } from './book_follow_up_meeting_button';
import { CancelMeetingModal } from './cancel_meeting_modal';
import { RescheduleButton } from './reschedule_button';
import { useRescheduleVisibility } from './use_reschedule_visibility';
import { getShowReschedule } from './utils';
const ADD_BUTTON_LOCATION = 'meetings_page_right_rail';
const actionsContainerCss = "a1l3occb";
const ActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsContainerCss, props.className)} />;
});
const contentContainerCss = "c13jellu";
const ContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)} />;
});
const buttonContainerCss = "b1eo2qkc";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const futureMeetingActionsRowCss = "fhkxmzx";

/* Prevent buttons from shrinking so row overflows when space is tight (enables overflow detection) */
const futureMeetingActionsRowItemCss = "f1rf4ncx";

/* On mobile allow buttons to scrunch; overflow detection is skipped and cancel is in menu only */
const futureMeetingActionsRowItemMobileCss = "f16uyhoo";
const FutureMeetingActionsRow = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(futureMeetingActionsRowCss, props.className)} />;
});
const summaryContainerCss = "s1m6k3h0";
const SummaryContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(summaryContainerCss, props.className)} />;
});
const SimpleRescheduleButton = ({
  onClick
}) => <Button decoration="secondary-outline" size="small" onClick={onClick}>
    <ArrowRoundRightIcon />
    {I18n.t('publisher.action.reschedule')}
  </Button>;
const CancelButton = ({
  openCancelModal,
  sendAnalytics
}) => {
  const handleClick = () => {
    if (sendAnalytics) sendAnalytics();
    openCancelModal();
  };
  return <Button size="small" decoration="secondary-danger" onClick={handleClick}>
      <DeleteIcon />
      {I18n.t('publisher.action.cancel')}
    </Button>;
};
const FutureMeetingActions = ({
  event,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  isNotPretending,
  inProgress,
  isCancelled,
  sendAnalyticsForReschedule,
  cancelFitsInRow = true,
  setCancelFitsInRow,
  onShowCancelInRowChange
}) => {
  const rowRef = useRef(null);
  const widthWhenOverflowRef = useRef(0);
  const prevRowCompositionRef = useRef(null);
  const isPhone = useDeviceType('isPhone');
  const {
    canAdd: canAddNotetaker,
    notetakerAdded
  } = useNotetaker();
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useBooleanState(false);
  const history = useHistory();
  const queryParams = useQueryParams();
  const {
    trigger_cancel: triggerCancel
  } = queryParams;
  const track = useTrack();
  const {
    showGenerateMeetingPrep,
    contact
  } = useGenerateMeetingPrep(event);

  /* Add Notetaker button is visible only when canAdd && !notetakerAdded; when hidden we have room for Cancel in row on phone */
  const addNotetakerButtonVisible = canAddNotetaker && !notetakerAdded;
  useEffect(() => {
    if (triggerCancel && event.uuid === triggerCancel && isCalendlyMeeting(event)) {
      openCancelModal();
      const modifiedParams = {
        ...queryParams
      };
      delete modifiedParams.trigger_cancel;
      history.push({
        search: buildQuery(modifiedParams)
      });
    }
  }, [triggerCancel, event, openCancelModal, queryParams, history]);
  const showReschedule = getShowReschedule(event);
  const showCancel = event.policy?.cancel && !inProgress && !isCancelled;
  const showCancelInRow = showCancel && cancelFitsInRow && (!isPhone || !addNotetakerButtonVisible);
  const otherFutureActionRowButtonCount = (addNotetakerButtonVisible ? 1 : 0) + (showReschedule ? 1 : 0) + ((isNotPretending ? showCancelInRow : showCancel) ? 1 : 0);
  const renderMeetingPrepBelow = isPhone && showGenerateMeetingPrep && otherFutureActionRowButtonCount >= 2;

  /* Sync showCancelInRow to parent before paint, and measure row overflow to set cancelFitsInRow.
   * Only set cancelFitsInRow(true) when the row has grown (width increased), so we avoid
   * a feedback loop: overflow → set false → cancel removed → row shrinks → observer fires →
   * no overflow → set true → cancel back → overflow again.
   * Reset the overflow-width guard when row composition changes (e.g. Add Notetaker or Reschedule
   * toggling) so Cancel can move back into the row when space frees up. */
  useLayoutEffect(() => {
    onShowCancelInRowChange?.(showCancelInRow);
    if (!setCancelFitsInRow || !showCancel || !rowRef.current) return undefined;
    const rowComposition = [showReschedule, addNotetakerButtonVisible, showGenerateMeetingPrep && !renderMeetingPrepBelow, isPhone];
    const prev = prevRowCompositionRef.current;
    if (prev && (prev[0] !== rowComposition[0] || prev[1] !== rowComposition[1] || prev[2] !== rowComposition[2] || prev[3] !== rowComposition[3])) {
      widthWhenOverflowRef.current = 0;
    }
    prevRowCompositionRef.current = rowComposition;
    const el = rowRef.current;
    const measure = () => {
      const overflow = el.scrollWidth > el.clientWidth;
      const first = el.firstElementChild;
      const last = el.lastElementChild;
      const wrapped = first && last && first !== last && last.getBoundingClientRect().top > first.getBoundingClientRect().top;
      if (overflow || wrapped) {
        setCancelFitsInRow(false);
        widthWhenOverflowRef.current = el.clientWidth;
      } else if (el.clientWidth > widthWhenOverflowRef.current) {
        setCancelFitsInRow(true);
        widthWhenOverflowRef.current = 0;
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showCancelInRow, onShowCancelInRowChange, showCancel, showReschedule, addNotetakerButtonVisible, showGenerateMeetingPrep, renderMeetingPrepBelow, isPhone, setCancelFitsInRow]);
  const rowItemClassName = classNames(futureMeetingActionsRowItemCss, isPhone && futureMeetingActionsRowItemMobileCss);

  /* Non-Calendly meetings have at most notetaker + meeting prep, so overflow on mobile is unlikely; skip the row-split logic. */
  if (!isCalendlyMeeting(event)) {
    return <FutureMeetingActionsRow ref={rowRef}>
        {addNotetakerButtonVisible && <span className={rowItemClassName}>
            <AddNotetakerButton event={event} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />
          </span>}
        {showGenerateMeetingPrep && <span className={rowItemClassName}>
            <GenerateMeetingPrep contact={contact} meeting={event} />
          </span>}
      </FutureMeetingActionsRow>;
  }
  const sendAnalyticsForCancel = () => track({
    name: 'Meeting Interaction',
    properties: {
      action: 'cancel',
      location: 'meetings_page_right_rail',
      event_uuid: event.uuid,
      mi_event_uuid: event.meetingIntelligence?.uuid,
      is_calendly_meeting: isCalendlyMeeting(event)
    }
  });
  return <>
      <ContentContainer>
        <FutureMeetingActionsRow ref={rowRef}>
          {addNotetakerButtonVisible && <span className={rowItemClassName}>
              <AddNotetakerButton event={event} invitee={event.invitee} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />
            </span>}
          {showReschedule && <span className={rowItemClassName}>
              <HostReschedule event={event} invitee={event.invitee} Button={SimpleRescheduleButton} sendAnalytics={sendAnalyticsForReschedule}>
                {/* This reschedule button isn't actually used based because we are on the new meetings page */}
                <RescheduleButton event={event} sendAnalytics={sendAnalyticsForReschedule} />
              </HostReschedule>
            </span>}
          {isNotPretending && showCancelInRow && <span className={rowItemClassName}>
              <CancelButton event={event} openCancelModal={openCancelModal} sendAnalytics={sendAnalyticsForCancel} />
            </span>}
          {/* Pretending users always get Cancel in the row when showCancel (no overflow/menu fallback). */}
          {!isNotPretending && showCancel && <span className={rowItemClassName}>
              <Button size="small" decoration="secondary-danger" onClick={openCancelModal}>
                <DeleteIcon />
                {I18n.t('publisher.action.cancel')}
              </Button>
            </span>}
          {showGenerateMeetingPrep && !renderMeetingPrepBelow && <span className={rowItemClassName}>
              <GenerateMeetingPrep contact={contact} meeting={event} />
            </span>}
        </FutureMeetingActionsRow>
        {renderMeetingPrepBelow && <FutureMeetingActionsRow>
            <span className={rowItemClassName}>
              <GenerateMeetingPrep contact={contact} meeting={event} />
            </span>
          </FutureMeetingActionsRow>}
      </ContentContainer>
      {isCancelModalOpen && <CancelMeetingModal event={event} onClose={closeCancelModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} showReportModal={showReportModal} />}
    </>;
};
const PastMeetingActions = ({
  event,
  isNotPretending
}) => {
  const {
    meetingIntelligence
  } = event;
  const hasSummary = meetingIntelligence && Boolean(meetingIntelligence?.summary);
  if (hasSummary) {
    return <ContextualBanner>
        <ContentContainer>
          <SummaryContainer>
            {HtmlParser(I18n.t('publisher.meetings.actions.in_this_meeting', {
            summary: meetingIntelligence?.summary
          }))}
          </SummaryContainer>
          <ButtonContainer>
            {isNotPretending && <AiRecapButtonComponent event={event} />}
            <BookFollowUpMeetingButton event={event} />
          </ButtonContainer>
        </ContentContainer>
      </ContextualBanner>;
  }
  return <>
      {isNotPretending && <AiRecapButtonComponent event={event} />}
      <BookFollowUpMeetingButton event={event} />
    </>;
};
const InProgressMeetingActions = ({
  event,
  isNotPretending,
  sendAnalyticsForReschedule
}) => {
  const track = useTrack();
  const {
    showRescheduleInRow
  } = useRescheduleVisibility(event, true);
  const eventLocation = getEventLocationIfURL(event.location);
  const onJoinClick = e => {
    e.stopPropagation();
    e.preventDefault();
    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: 'join',
        location: 'meetings_page_right_rail',
        event_uuid: event.uuid,
        mi_event_uuid: event?.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(event)
      }
    });
    window.open(eventLocation, '_blank', 'noopener,noreferrer');
  };
  return <>
      {eventLocation && <Button onClick={onJoinClick} size="small" responsive={false}>
          {I18n.t('publisher.meetings.actions.join_meeting')}
        </Button>}
      <AddNotetakerButton event={event} buttonLocation={ADD_BUTTON_LOCATION} isNotPretending={isNotPretending} />
      {showRescheduleInRow && <HostReschedule event={event} invitee={event.invitee} Button={SimpleRescheduleButton} sendAnalytics={sendAnalyticsForReschedule}>
          <RescheduleButton event={event} sendAnalytics={sendAnalyticsForReschedule} />
        </HostReschedule>}
    </>;
};
const AiRecapButtonComponent = ({
  event
}) => {
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  if (!isNotetakerEntitled) return null;
  return <AiRecapButton event={event} location="meetings_page_right_rail" />;
};
const ActionsLoadedComponent = ({
  event,
  isFutureMeeting,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  sendAnalyticsForReschedule,
  isNotPretending,
  isPast,
  isCancelled,
  inProgress,
  cancelFitsInRow,
  setCancelFitsInRow,
  onShowCancelInRowChange
}) => {
  const {
    isLoading
  } = useNotetaker();
  if (isLoading) {
    return <ActionsContainer>
        <Loader size="small" inline="true" />
      </ActionsContainer>;
  }
  return <ActionsComponent event={event} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} sendAnalyticsForReschedule={sendAnalyticsForReschedule} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} cancelFitsInRow={cancelFitsInRow} setCancelFitsInRow={setCancelFitsInRow} onShowCancelInRowChange={onShowCancelInRowChange} />;
};
const ActionsComponent = ({
  event,
  isFutureMeeting,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  sendAnalyticsForReschedule,
  isNotPretending,
  isPast,
  isCancelled,
  inProgress,
  cancelFitsInRow,
  setCancelFitsInRow,
  onShowCancelInRowChange
}) => <ActionsContainer>
    {isFutureMeeting && <FutureMeetingActions event={event} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} isNotPretending={isNotPretending} inProgress={inProgress} isCancelled={isCancelled} sendAnalyticsForReschedule={sendAnalyticsForReschedule} cancelFitsInRow={cancelFitsInRow} setCancelFitsInRow={setCancelFitsInRow} onShowCancelInRowChange={onShowCancelInRowChange} />}
    {inProgress && <InProgressMeetingActions event={event} isNotPretending={isNotPretending} sendAnalyticsForReschedule={sendAnalyticsForReschedule} />}
    {isPast && <PastMeetingActions event={event} isNotPretending={isNotPretending} />}
  </ActionsContainer>;
export const Actions = ({
  event,
  clearSelectedEvent,
  showReportModal,
  updateSelectedEvent,
  sendAnalyticsForReschedule,
  inProgress,
  isFutureMeeting,
  isNotPretending = true,
  isPast,
  isCancelled,
  cancelFitsInRow,
  setCancelFitsInRow,
  onShowCancelInRowChange
}) => {
  if (isNotPretending) {
    return <ActionsLoadedComponent event={event} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} sendAnalyticsForReschedule={sendAnalyticsForReschedule} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} cancelFitsInRow={cancelFitsInRow} setCancelFitsInRow={setCancelFitsInRow} onShowCancelInRowChange={onShowCancelInRowChange} />;
  }
  return <ActionsComponent event={event} isFutureMeeting={isFutureMeeting} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} sendAnalyticsForReschedule={sendAnalyticsForReschedule} isNotPretending={isNotPretending} isPast={isPast} isCancelled={isCancelled} inProgress={inProgress} cancelFitsInRow={cancelFitsInRow} setCancelFitsInRow={setCancelFitsInRow} onShowCancelInRowChange={onShowCancelInRowChange} />;
};

require("./actions.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./actions.js");